import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { TextB, TextItalic, ListBullets, ListNumbers, LinkSimple } from "@phosphor-icons/react";

const isEmpty = (html) => !html || html === "<p></p>" || html.replace(/<[^>]*>/g, "").trim() === "";

const ToolbarBtn = ({ active, onClick, label, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    aria-label={label}
    className={`grid h-8 w-8 place-items-center rounded-md transition-colors ${
      active ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-100"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder = "Write here…", testid }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false, link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[#F27A54] underline" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[96px] px-3 py-2 outline-none",
        "data-testid": testid ? `${testid}-area` : undefined,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(isEmpty(html) ? "" : html);
    },
  });

  // Sync external changes (e.g. AI pre-fill) into the editor without clobbering typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current && (isEmpty(current) || isEmpty(next))) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rounded-md border border-border bg-white focus-within:ring-1 focus-within:ring-[#F27A54]" data-testid={testid}>
      <div className="flex items-center gap-0.5 border-b border-border px-1.5 py-1">
        <ToolbarBtn testid={testid && `${testid}-bold`} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold"><TextB size={16} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={testid && `${testid}-italic`} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic"><TextItalic size={16} weight="bold" /></ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarBtn testid={testid && `${testid}-bullet`} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list"><ListBullets size={16} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={testid && `${testid}-ordered`} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list"><ListNumbers size={16} weight="bold" /></ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarBtn testid={testid && `${testid}-link`} active={editor.isActive("link")} onClick={setLink} label="Link"><LinkSimple size={16} weight="bold" /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
